import { z } from 'zod';
import { DemandState } from '~/enums/_types';
import { zDbId, zLuxonDateTime, zNullishDefault } from '~/plugins/zod/utils';
import {
    AppUserSimpleSchema,
    CategoryItemDetail,
    CategoryItemDetailSchema,
    OrderLinkSchema,
    PartnerSimpleSchema,
    Price,
    PriceSchema,
} from '../links/_types';
import { API_DATE_FORMAT } from '~/constants/_constants';
import { NomenclatureConfigSnapshotInfoSchema } from '../items/_types';

export const DemandInfoSchema = z.object({
    Id: zDbId(),
    ReferenceNumber: z.string().nullable(),
    Partner: PartnerSimpleSchema,
    Customer: AppUserSimpleSchema,
    CreatedAt: zLuxonDateTime({ iso: true }),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Price: PriceSchema.nullable(),
    Status: z.nativeEnum(DemandState),
    Attachment: z.string().nullable(),
    NextStates: z.nativeEnum(DemandState).array(),
});

export const DemandDetailLineConfigSchema = z.object({
    RuleId: zDbId(),
    OptionId: zDbId(),
});

export const DemandDetailLineInfoSchema = z.object({
    Id: zDbId(),
    Item: CategoryItemDetailSchema,
    Quantity: z.number(),
    Nomenclature: z.string().nullable(),
    Configuration: NomenclatureConfigSnapshotInfoSchema.array(),
    Price: PriceSchema.nullable(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
});

export const DemandDetailSchema = z.object({
    Id: zDbId(),
    ReferenceNumber: z.string().nullable(),
    CreatedAt: zLuxonDateTime({ iso: true }),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Price: PriceSchema,
    Partner: PartnerSimpleSchema,
    Customer: AppUserSimpleSchema,
    Orders: OrderLinkSchema.array(),
    Status: z.nativeEnum(DemandState),
    Attachment: z.string().nullable(),
    NextStates: z.nativeEnum(DemandState).array(),
    Note: z.string().nullable(),
    Address: z.string(),
    Lines: DemandDetailLineInfoSchema.array(),
    Can: z.object({
        CreateOrder: z.boolean(),
    }),
});

export const DemandDraftLineConfigSchema = z.object({
    RuleId: zDbId(),
    OptionId: zDbId(),
});

export const DemandDraftLineInfoSchema = z.object({
    Id: zDbId(),
    Item: CategoryItemDetailSchema,
    Quantity: z.number(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Nomenclature: z.string().nullable(),
    Price: PriceSchema.nullable(),
    Configuration: zNullishDefault(DemandDraftLineConfigSchema.array(), []),
});

export const DemandDraftDetailSchema = z.object({
    Id: zDbId(),
    Partner: PartnerSimpleSchema,
    ReferenceNumber: z.string().nullable(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Address: z.string().nullable(),
    Note: z.string().nullable(),
    Lines: zNullishDefault(DemandDraftLineInfoSchema.array(), []),
});

export const DemandItemConfigFormDataSchema = z.object({
    RuleId: z.number(),
    OptionId: z.number(),
});

export const DemandLineFormDataSchema = z.object({
    Id: z.string(),
    // pouze pro FE, custom schema bude skipovat validaci pro tuto property
    Item: z.custom<CategoryItemDetail>(),
    Quantity: z.number().min(1),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullish(),
    Configuration: DemandItemConfigFormDataSchema.array(),
    CategoryId: z.number().nullish(),
    Nomenclature: z.custom<string | null>(),
    Price: z.custom<Price | null>(),
});

export const DemandFormDataSchema = z.object({
    Id: z.number().nullish(),
    ReferenceNumber: z.string().nullish(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullish(),
    Address: z.string().nullish(),
    Note: z.string().nullish(),
    Lines: DemandLineFormDataSchema.array(),
});

export const FetchDemandsResponseSchema = DemandInfoSchema.array();

export type DemandInfo = z.infer<typeof DemandInfoSchema>;
export type FetchDemandsResponse = z.infer<typeof FetchDemandsResponseSchema>;
export type DemandDraftLineInfo = z.infer<typeof DemandDraftLineInfoSchema>;
export type DemandDraftDetail = z.infer<typeof DemandDraftDetailSchema>;
export type DemandDetail = z.infer<typeof DemandDetailSchema>;
export type DemandDetailLineInfo = z.infer<typeof DemandDetailLineInfoSchema>;
export type DemandLineFormData = z.infer<typeof DemandLineFormDataSchema>;
export type DemandFormData = z.infer<typeof DemandFormDataSchema>;
